// ABOUTME: Centralizes pair-aware State/City selection identity, validation, filtering, and pruning.
// ABOUTME: Keeps opaque Elasticsearch values unchanged across UI and persisted filter round-trips.

export type StateSelection =
    { kind: "value"; value: string } | { kind: "missing" };

export type CitySelection =
    | { kind: "pair"; value: string; parentValue: string | null }
    | { kind: "missing" };

export type LocationSelection = StateSelection | CitySelection;

export const getListSearchMetadataGate = (
    moduleName: string,
    metadataStatus: string | undefined
) => {
    const requiresMetadata = moduleName === "JOBLIST";
    return {
        executeJustOnCall: requiresMetadata,
        canExecuteSearch:
            requiresMetadata === false || metadataStatus === "success",
        metadataError: requiresMetadata && metadataStatus === "error"
    };
};

export const isDependentLocationMetadataEnabled = (
    definition: Compleo.IObject | undefined | null
) => {
    const buckets = definition?.buckets || definition || {};
    return (
        buckets.state?.type === "list" &&
        buckets.state?.fieldName === "location.provinceOrState" &&
        buckets.state?.groupByValue === true &&
        buckets.state?.returnMissing === true &&
        buckets.city?.type === "list" &&
        buckets.city?.fieldName === "location.city" &&
        buckets.city?.groupByValue === true &&
        buckets.city?.returnMissing === true &&
        buckets.city?.dependsOn?.bucket === "state" &&
        buckets.city?.dependsOn?.parentFieldName ===
            "location.provinceOrState" &&
        buckets.city?.dependsOn?.identity === "parentValueAndValue"
    );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === "object" && !Array.isArray(value);

const hasLocationSelectionDiscriminant = (value: unknown) =>
    isRecord(value) && Object.prototype.hasOwnProperty.call(value, "kind");

export const isStateSelection = (value: unknown): value is StateSelection => {
    if (!isRecord(value)) return false;
    if (value.kind === "missing") return Object.keys(value).length === 1;
    return (
        value.kind === "value" &&
        typeof value.value === "string" &&
        Object.keys(value).every((key) => ["kind", "value"].includes(key))
    );
};

export const isCitySelection = (value: unknown): value is CitySelection => {
    if (!isRecord(value)) return false;
    if (value.kind === "missing") return Object.keys(value).length === 1;
    return (
        value.kind === "pair" &&
        typeof value.value === "string" &&
        (typeof value.parentValue === "string" || value.parentValue === null) &&
        Object.keys(value).every((key) =>
            ["kind", "value", "parentValue"].includes(key)
        )
    );
};

export const isDependentLocationSelection = (
    bucketName: string,
    selection: unknown
) =>
    (bucketName === "state" && isStateSelection(selection)) ||
    (bucketName === "city" && isCitySelection(selection));

export const getSelectionKey = (
    bucketName: string,
    selection: unknown
): string => {
    if (bucketName === "city" && isCitySelection(selection)) {
        return selection.kind === "missing"
            ? JSON.stringify(["missing"])
            : JSON.stringify(["pair", selection.parentValue, selection.value]);
    }
    if (bucketName === "state" && isStateSelection(selection)) {
        return selection.kind === "missing"
            ? JSON.stringify(["missing"])
            : JSON.stringify(["value", selection.value]);
    }
    return JSON.stringify([
        "legacy",
        isRecord(selection) ? selection.value : selection
    ]);
};

export const getBucketSelection = (
    bucketName: string,
    bucket: Compleo.IObject
): unknown => {
    if (isDependentLocationSelection(bucketName, bucket.selection)) {
        return bucket.selection;
    }
    return { value: bucket.value, postData: bucket.postData };
};

export const selectionsEqual = (
    bucketName: string,
    left: unknown,
    right: unknown
) => getSelectionKey(bucketName, left) === getSelectionKey(bucketName, right);

export const isBucketSelected = (params: {
    bucketName: string;
    values: unknown[];
    bucket: Compleo.IObject;
}) => {
    const selection = getBucketSelection(params.bucketName, params.bucket);
    return params.values.some((value) =>
        selectionsEqual(params.bucketName, value, selection)
    );
};

export const getBucketOptionKey = (
    bucketName: string,
    bucket: Compleo.IObject
) => getSelectionKey(bucketName, getBucketSelection(bucketName, bucket));

const getSelectedStateParents = (stateValues: StateSelection[]) =>
    new Set(
        stateValues.map((selection) =>
            selection.kind === "missing" ? null : selection.value
        )
    );

export const filterDependentCityOptions = (
    items: Compleo.IObject[],
    buckets: Compleo.IObject
) => {
    const stateValues = (buckets?.state?.values || []).filter(isStateSelection);
    if (stateValues.length === 0) return items;

    const parents = getSelectedStateParents(stateValues);
    return items
        .map((item) => {
            const selection = item.selection;
            if (!isCitySelection(selection)) return null;
            if (selection.kind === "pair") {
                return parents.has(selection.parentValue) ? item : null;
            }
            if (!Array.isArray(item.missingByParent)) return item;
            return {
                ...item,
                total: item.missingByParent.reduce(
                    (total: number, entry: Compleo.IObject) =>
                        parents.has(entry.parentValue)
                            ? total + Number(entry.total || 0)
                            : total,
                    0
                )
            };
        })
        .filter((item): item is Compleo.IObject => item !== null);
};

export const normalizeDependentLocationBuckets = (
    buckets: Compleo.IObject | undefined,
    enabled = false
): { buckets: Compleo.IObject; discarded: number } => {
    const normalized = { ...(buckets || {}) };
    let discarded = 0;

    if (!enabled) {
        for (const bucketName of ["state", "city"]) {
            const bucket = normalized[bucketName];
            if (!bucket || !Array.isArray(bucket.values)) continue;
            const values = bucket.values.filter(
                (selection: unknown) =>
                    !hasLocationSelectionDiscriminant(selection)
            );
            discarded += bucket.values.length - values.length;
            if (values.length > 0)
                normalized[bucketName] = { ...bucket, values };
            else delete normalized[bucketName];
        }
        return { buckets: normalized, discarded };
    }

    if (normalized.state) {
        const hasValuesArray = Array.isArray(normalized.state.values);
        const original = hasValuesArray ? normalized.state.values : [];
        if (!hasValuesArray) discarded += 1;
        const values = original.filter(isStateSelection);
        discarded += original.length - values.length;
        if (values.length > 0) normalized.state = { values };
        else delete normalized.state;
    }

    if (normalized.city) {
        const hasValuesArray = Array.isArray(normalized.city.values);
        const original = hasValuesArray ? normalized.city.values : [];
        if (!hasValuesArray) discarded += 1;
        let values: CitySelection[] = original.filter(isCitySelection);
        discarded += original.length - values.length;

        const stateValues: StateSelection[] = normalized.state?.values || [];
        if (stateValues.length > 0) {
            const parents = getSelectedStateParents(stateValues);
            values = values.filter((selection) =>
                selection.kind === "missing"
                    ? true
                    : parents.has(selection.parentValue)
            );
        }

        if (values.length > 0) normalized.city = { values };
        else delete normalized.city;
    }

    return { buckets: normalized, discarded };
};

export const normalizeDependentLocationFilters = <T extends Compleo.IObject>(
    filters: T,
    enabled = false
): { filters: T; discarded: number } => {
    const result = normalizeDependentLocationBuckets(filters?.buckets, enabled);
    return {
        filters: { ...filters, buckets: result.buckets },
        discarded: result.discarded
    };
};

export const changeBucketSelection = <T extends Compleo.IObject>(params: {
    selected: boolean;
    bucketName: string;
    filters: T;
    bucket: Compleo.IObject;
}) => {
    const newFilters = {
        ...params.filters,
        buckets: { ...(params.filters?.buckets || {}) }
    };
    const currentValues = [
        ...(newFilters.buckets[params.bucketName]?.values || [])
    ];
    const selection = getBucketSelection(params.bucketName, params.bucket);

    const isDependentBucket = isDependentLocationSelection(
        params.bucketName,
        selection
    );
    if (!isDependentBucket) {
        const values = params.selected
            ? currentValues.some((item) => item.value === params.bucket.value)
                ? currentValues
                : [
                      ...currentValues,
                      {
                          value: params.bucket.value,
                          postData: params.bucket.postData
                      }
                  ]
            : currentValues.filter(
                  (item) => item.value !== params.bucket.value
              );
        if (values.length > 0) {
            newFilters.buckets[params.bucketName] = { values };
        } else {
            delete newFilters.buckets[params.bucketName];
        }
        return newFilters;
    }

    const predicate =
        params.bucketName === "state" ? isStateSelection : isCitySelection;
    if (!predicate(selection)) return newFilters;

    const values = params.selected
        ? currentValues.some((item) =>
              selectionsEqual(params.bucketName, item, selection)
          )
            ? currentValues
            : [...currentValues, selection]
        : currentValues.filter(
              (item) => !selectionsEqual(params.bucketName, item, selection)
          );

    if (values.length > 0) {
        newFilters.buckets[params.bucketName] = { values };
    } else {
        delete newFilters.buckets[params.bucketName];
        if (params.bucketName === "state") delete newFilters.buckets.city;
    }

    return normalizeDependentLocationFilters(newFilters, true).filters;
};

export const findBucketOption = (
    bucketName: string,
    items: Compleo.IObject[],
    selection: unknown
) =>
    items.find((item) =>
        selectionsEqual(
            bucketName,
            getBucketSelection(bucketName, item),
            selection
        )
    );

export const getSelectionFallbackLabel = (
    bucketName: string,
    selection: unknown,
    missingLabel: string
) => {
    if (bucketName === "state" && isStateSelection(selection)) {
        return selection.kind === "missing" ? missingLabel : selection.value;
    }
    if (bucketName === "city" && isCitySelection(selection)) {
        if (selection.kind === "missing") return missingLabel;
        return `${selection.value} — ${
            selection.parentValue === null
                ? missingLabel
                : selection.parentValue
        }`;
    }
    return String(isRecord(selection) ? (selection.value ?? "") : "");
};

export const getDependentBucketChipInfo = (params: {
    bucketName: "state" | "city";
    items: Compleo.IObject[];
    selection: StateSelection | CitySelection;
    missingLabel: string;
}) => {
    const option = findBucketOption(
        params.bucketName,
        params.items,
        params.selection
    );
    return {
        label:
            option?.label ||
            getSelectionFallbackLabel(
                params.bucketName,
                params.selection,
                params.missingLabel
            ),
        valueKey: params.selection
    };
};

export const getVisibleBucketItems = (
    aggregation: Compleo.IObject,
    selectedBuckets: Compleo.IObject
) => {
    const dependentItems =
        aggregation.key === "city" &&
        aggregation.dependentLocationEnabled === true &&
        aggregation.groupByValue === true &&
        aggregation.selectionIdentity === "parentValueAndValue"
            ? filterDependentCityOptions(aggregation.items, selectedBuckets)
            : aggregation.items;
    const sortedItems = [...dependentItems];
    if (aggregation.sortAlphabetically === true) {
        sortedItems.sort((left: Compleo.IObject, right: Compleo.IObject) =>
            left?.label.toLowerCase().localeCompare(right?.label.toLowerCase())
        );
    }
    return sortedItems.filter((item: Compleo.IObject) => item.total > 0);
};

export const removeToolbarBucketSelection = <
    T extends Compleo.IObject
>(params: {
    valueKey: unknown;
    descriptionKey: string;
    filters: T;
}): T => ({
    ...changeBucketSelection({
        selected: false,
        bucketName: params.descriptionKey,
        filters: params.filters,
        bucket: isDependentLocationSelection(
            params.descriptionKey,
            params.valueKey
        )
            ? { selection: params.valueKey }
            : { value: params.valueKey }
    }),
    filterUpdated: true
});

const normalizeFiltersForDefinition = <T extends Compleo.IObject>(
    filters: T,
    definition: Compleo.IObject | null | undefined,
    metadataResolved = false
) => {
    if (definition == null && !metadataResolved) {
        return { filters, discarded: 0 };
    }
    return normalizeDependentLocationFilters(
        filters,
        isDependentLocationMetadataEnabled(definition)
    );
};

export const normalizeProviderFilters = normalizeFiltersForDefinition;
export const normalizeDefaultFilterForLoad = normalizeFiltersForDefinition;
export const normalizeSavedFilterForLoad = normalizeFiltersForDefinition;
